import type { DocumentDetail } from '@avisoma/shared';
import CopyButton from '../../components/CopyButton';
import FileSize from '../../components/FileSize';
import StatusIndicator from '../../components/StatusIndicator';
import TagEditor from '../../components/TagEditor';
import { formatDateTime } from '../../lib/dateFormat';
import backend from '../../services/backend';

interface DocumentInfoCardProps {
  document: DocumentDetail;
  onTagsSaved: (updatedDocument: DocumentDetail) => void;
  /** Segmentation breakdown derived from the page list: how many original PDF
   *  pages there were and how many of the current pages came from section splits. */
  segmentation?: { originalPages: number; parentCount: number; childCount: number };
}

export function DocumentInfoCard({ document, onTagsSaved, segmentation }: DocumentInfoCardProps) {
  return (
    <div className="bg-white dark:bg-zinc-800 shadow rounded-lg overflow-hidden">
      <div className="px-4 py-5 sm:p-6 border-b border-gray-200 dark:border-zinc-700">
        <h2 className="text-lg font-medium text-gray-900 dark:text-zinc-100">Document Information</h2>
      </div>
      <div className="px-4 py-5 sm:p-6 space-y-4">
        <div>
          <dt className="text-sm font-medium text-gray-500 dark:text-zinc-400">Document ID</dt>
          <dd className="mt-1 text-sm text-gray-900 dark:text-zinc-100 font-mono flex items-center gap-2 min-w-0">
            <span className="truncate" title={document.id}>
              {document.id}
            </span>
            <CopyButton className="shrink-0" text={document.id} />
          </dd>
        </div>
        <div>
          <dt className="text-sm font-medium text-gray-500 dark:text-zinc-400">File Name</dt>
          <dd
            className="mt-1 text-sm text-gray-900 dark:text-zinc-100 truncate"
            title={document.fileName}
          >
            {document.fileName}
          </dd>
        </div>
        <div>
          <dt className="text-sm font-medium text-gray-500 dark:text-zinc-400">MIME Type</dt>
          <dd className="mt-1 text-sm text-gray-900 dark:text-zinc-100">{document.mimeType}</dd>
        </div>
        <div>
          <dt className="text-sm font-medium text-gray-500 dark:text-zinc-400">File Size</dt>
          <dd className="mt-1 text-sm text-gray-900 dark:text-zinc-100">
            <FileSize bytes={document.size} />
          </dd>
        </div>
        {document.processing?.pages?.status === 'done' && (
          <div>
            <dt className="text-sm font-medium text-gray-500 dark:text-zinc-400">Number of pages</dt>
            <dd className="mt-1 text-sm text-gray-900 dark:text-zinc-100 flex items-center gap-2">
              {document.processing.pages.pageCount}
              <span className="text-gray-500 dark:text-zinc-500">
                ({document.processing.pages.uniquePageCount} unique)
              </span>
            </dd>
            {segmentation && (
              <dd className="mt-1 text-xs text-gray-500 dark:text-zinc-400">
                {segmentation.originalPages} original
                {segmentation.childCount > 0
                  ? ` · ${segmentation.parentCount} split into ${segmentation.childCount} segments`
                  : ' · no segmentation'}
              </dd>
            )}
          </div>
        )}
        <div>
          <dt className="text-sm font-medium text-gray-500 dark:text-zinc-400">Status</dt>
          <dd className="mt-1 flex items-center gap-2 text-sm text-gray-900 dark:text-zinc-100">
            <StatusIndicator status={document.status} />
            {document.status}
            {document.processing?.durationSeconds ? (
              <span className="text-gray-500 dark:text-zinc-500">
                in <DoneInTime time={document.processing.durationSeconds} />
              </span>
            ) : (
              document.processing?.startedAt && (
                <span className="text-gray-500 dark:text-zinc-500">
                  started{' '}
                  <DoneInTime
                    time={
                      (new Date().getTime() - new Date(document.processing.startedAt).getTime()) /
                      1000
                    }
                  />{' '}
                  ago
                </span>
              )
            )}
          </dd>
        </div>
        <div>
          <dt className="text-sm font-medium text-gray-500 dark:text-zinc-400">Created</dt>
          <dd className="mt-1 text-sm text-gray-900 dark:text-zinc-100">
            {formatDateTime(document.createdAt)}
          </dd>
        </div>
        <div>
          <dt className="text-sm font-medium text-gray-500 dark:text-zinc-400">Updated</dt>
          <dd className="mt-1 text-sm text-gray-900 dark:text-zinc-100">
            {formatDateTime(document.updatedAt)}
          </dd>
        </div>
        <div>
          <TagEditor
            tags={document.tags}
            onSave={async (tags) => {
              const response = await backend.updateDocument(document.id, { tags });
              onTagsSaved(response.data);
            }}
          />
        </div>
      </div>
    </div>
  );
}

function DoneInTime({ time }: { time: number | undefined }) {
  if (!time) return null;
  if (time >= 60) return `${(time / 60).toFixed(1)} minutes`;
  return `${time.toFixed(1)} seconds`;
}
